// ── App.js ───────────────────────────────────────────────────────────────────
// CHANGES FROM ORIGINAL:
//   1. Import TrainingPage
//   2. Pass currentUser={user} to <TrainingPage> so enrollment pre-fills
//      name/email and the backend can upgrade their role to "trainee"

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Navbar from "./components/Navbar";
import HomePage from "./components/HomePage";
import JobsPage from "./components/JobsPage";
import TrainingPage from "./components/TrainingPage/TrainingPage";
import AdminDashboard from "./components/Dashboards/AdminDashboard/AdminDashboard";
import InterviewerDashboard from "./components/InterviewerDashboard";
import UserDashboard from "./components/UserDashboard";
import AuthModal from "./components/AuthModal";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./utils/ScrollToTop";
import DiscordPage from "./components/DiscordPage/DiscordPage";
import { Analytics } from "@vercel/analytics/react";
import { ExternalRedirect } from "./utils/externalRedirect";
/* ── Toast hook ───────────────────────────────────────────────────────────── */
function useNotify() {
  const [toast, setToast] = useState(null);
  const notify = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return [toast, notify];
}

/* ── Toast UI ─────────────────────────────────────────────────────────────── */
function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        padding: "11px 18px",
        borderRadius: 10,
        fontSize: ".875rem",
        fontWeight: 600,
        background: "var(--surface)",
        border: `1px solid ${toast.type === "error" ? "rgba(239,68,68,.35)" : "rgba(34,197,94,.35)"}`,
        color: toast.type === "error" ? "var(--red)" : "var(--green)",
        boxShadow: "0 8px 32px rgba(0,0,0,.35)",
        maxWidth: 340,
        pointerEvents: "none",
        animation: "toast-in .22s cubic-bezier(.16,1,.3,1)",
      }}
    >
      {toast.msg}
    </div>
  );
}

/* ── Main App ─────────────────────────────────────────────────────────────── */
export default function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("ql_user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [showAuth, setShowAuth] = useState(null); // "login" | "register" | null
  const [toast, notify] = useNotify();

  // Arrival from the api.js session-expiry redirect: prompt a fresh login.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("session") === "expired") {
      params.delete("session");
      const query = params.toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}`,
      );
      setUser(null);
      setShowAuth("login");
      notify("Your session has expired. Please log in again.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem("ql_user", JSON.stringify(userData));
    localStorage.setItem("ql_token", token);
    setUser(userData);
    setShowAuth(null);
  };

  // Profile changes made anywhere (e.g. renaming yourself while applying) are
  // merged into the session so the navbar/dashboard don't show a stale name.
  const updateUser = (patch) => {
    if (!patch) return;
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem("ql_user", JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem("ql_user");
    localStorage.removeItem("ql_token");
    setUser(null);
  };

  return (
    <BrowserRouter>
      <Analytics />
      <ScrollToTop />
      <Toast toast={toast} />

      {showAuth && (
        <AuthModal
          mode={showAuth}
          onClose={() => setShowAuth(null)}
          onLogin={handleLogin}
          setMode={setShowAuth}
          notify={notify}
        />
      )}

      <Routes>
        {/* ── Public pages ──────────────────────────────────────────────── */}
        <Route
          path="/"
          element={
            <>
              <Navbar
                currentUser={user}
                setShowAuth={setShowAuth}
                logout={logout}
              />
              <HomePage setShowAuth={setShowAuth} currentUser={user} />
            </>
          }
        />
        <Route
          path="/discord"
          element={
            <>
              <Navbar
                currentUser={user}
                setShowAuth={setShowAuth}
                logout={logout}
              />
              <DiscordPage />
            </>
          }
        />

        <Route
          path="/jobs"
          element={
            <>
              <Navbar
                currentUser={user}
                setShowAuth={setShowAuth}
                logout={logout}
              />
              <JobsPage
                user={user}
                setShowAuth={setShowAuth}
                notify={notify}
                onUserUpdate={updateUser}
              />
            </>
          }
        />

        {/* ✅ Training program page — currentUser passed so payment form
            pre-fills name/email and role update to "trainee" works */}
        <Route
          path="/training"
          element={
            <>
              <Navbar
                currentUser={user}
                setShowAuth={setShowAuth}
                logout={logout}
              />
              <TrainingPage setShowAuth={setShowAuth} currentUser={user} />
            </>
          }
        />

        {/* ── Admin panel — admin only ───────────────────────────────────── */}
        <Route
          path="/admin-panel"
          element={
            <ProtectedRoute user={user} adminOnly={true}>
              <AdminDashboard user={user} logout={logout} notify={notify} />
            </ProtectedRoute>
          }
        />

        {/* ── Interviewer panel — interviewer (and admin) only ──────────── */}
        <Route
          path="/interviewer-panel"
          element={
            <ProtectedRoute user={user} interviewerAllowed={true}>
              <InterviewerDashboard user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* ── User dashboard — regular users only ───────────────────────── */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute user={user} userOnly={true}>
              <UserDashboard
                user={user}
                logout={logout}
                notify={notify}
                onUserUpdate={updateUser}
              />
            </ProtectedRoute>
          }
        />

        {/* ── Catch-all ─────────────────────────────────────────────────── */}
        <Route path="*" element={<Navigate to="/" replace />} />

        <Route
          path="/polycode"
          element={
            <ExternalRedirect url="https://code.quantumlogicslimited.com" />
          }
        />
        <Route
          path="/boolforge"
          element={
            <ExternalRedirect url="https://circuits.quantumlogicslimited.com" />
          }
        />
        <Route
          path="/calculus"
          element={
            <ExternalRedirect url="https://calculus.quantumlogicslimited.com" />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
